import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import ModuleBuilder from '../../components/teacher/ModuleBuilder';
import ModuleCard from '../../components/teacher/ModuleCard';
import Modal from '../../components/common/Modal';
import { BookOpenIcon, PlusIcon } from '@heroicons/react/24/outline';


const ModuleList = ({ courseId, initialModules = null }) => {
    const navigate = useNavigate();
    const { get, put, del } = useApi();
    const { showToast } = useAppState();
    const [modules, setModules] = useState([]);
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showBuilder, setShowBuilder] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [strandToDelete, setStrandToDelete] = useState(null);



    const fetchModules = useCallback(async () => {
        // Optimization: Skip fetch if initialModules are provided
        if (initialModules && initialModules.length > 0 && !loading) {
            setModules(initialModules);
            return;
        }

        try {
            setLoading(true);
            const courseResponse = await get(`/teachers/api/courses/${courseId}/`);
            setModules(courseResponse.modules || []);
            setCourse(courseResponse);
        } catch (error) {
            console.error('Error fetching modules:', error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseId, get, initialModules]);



    useEffect(() => {
        if (initialModules && initialModules.length > 0) {
            setModules(initialModules);
            setLoading(false);
        } else {
            fetchModules();
        }
    }, [initialModules, fetchModules]);

    const handleCreateModule = () => {
        navigate(`/teacher/curriculum-manager/${courseId}`);
    };

    const handleEditModule = (module) => {
        setEditingModule(module);
        setShowBuilder(true);
    };

    const handleDeleteModule = (module) => {
        setStrandToDelete(module);
    };

    const confirmDeleteStrand = async () => {
        if (!strandToDelete) return;

        try {
            // Use the CBC API DELETE endpoint - matches CurriculumAreaManager pattern
            // Passing learning_area ensures national registry items are hidden not permanently deleted
            await del(`/api/cbc/strands/${strandToDelete.id}/?learning_area=${courseId}`);
            showToast(`"${strandToDelete.title || strandToDelete.name}" removed successfully`, 'success');
            setStrandToDelete(null);
            fetchModules();
        } catch (error) {
            console.error('Error deleting strand:', error);
            showToast('Failed to remove strand', 'error');
        }
    };


    const handleMoveStrand = async (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === modules.length - 1) return;

        const newModules = [...modules];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        
        // Swap
        [newModules[index], newModules[targetIndex]] = [newModules[targetIndex], newModules[index]];
        
        // Optimistic update
        setModules(newModules);

        const updates = newModules.map((module, idx) => ({
            id: module.id,
            order: idx + 1
        }));

        try {
            const response = await put(`/teachers/api/courses/${courseId}/modules/reorder/`, { modules: updates });
            
            // Sync with backend confirmation if needed
            if (response && response.status === 'success' && response.modules) {
                const orderMap = {};
                response.modules.forEach(m => { orderMap[m.id] = m.order; });
                
                setModules(prev => prev.map(m => ({
                    ...m,
                    order: orderMap[m.id] || m.order
                })).sort((a, b) => a.order - b.order));
                
                showToast('Order saved', 'success');
            }
        } catch (error) {
            console.error('Error reordering strands:', error);
            showToast('Failed to save order', 'error');
            fetchModules(); // Rollback on actual error
        }
    };




    if (loading && !course) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Desktop Header */}
            <div className="hidden md:flex flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                <div>
                    <h2 className="text-2xl font-black text-[#18216D] tracking-tight">Curriculum Strands</h2>
                    <p className="text-slate-400 text-sm font-medium">
                        Organize your learning area into major thematic strands
                    </p>
                </div>
                <button
                    onClick={handleCreateModule}
                    className="px-6 py-3 bg-[#18216D] text-white rounded-xl hover:bg-[#0D164F] transition-all font-black shadow-xl shadow-indigo-900/20 flex items-center space-x-3 text-[10px] uppercase tracking-widest"
                >
                    <PlusIcon className="w-5 h-5 stroke-[4]" />
                    <span>Edit Curriculum</span>
                </button>
            </div>

            {/* Mobile Header */}
            <div className="md:hidden space-y-6">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-1 w-8 bg-[#FFC425] rounded-full"></div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Curriculum Mapping</p>
                    </div>
                    <h1 className="text-4xl font-black text-[#18216D] tracking-tighter">Learning Strands</h1>
                </div>

                <button
                    onClick={handleCreateModule}
                    className="w-full py-4 bg-[#18216D] text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-900/20 flex items-center justify-center gap-3 active:scale-95 transition-all"
                >
                    <PlusIcon className="w-5 h-5 stroke-[3]" />
                    <span>Edit Curriculum</span>
                </button>
            </div>

            {/* Content List */}
            {modules.length === 0 ? (
                <div className="bg-white rounded-3xl border-2 border-dashed border-gray-200 p-12 text-center">
                    <div className="flex justify-center mb-6">
                        <BookOpenIcon className="h-16 w-16 text-gray-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No Strands Yet</h3>
                    <p className="text-slate-400 max-w-sm mx-auto mb-8 font-medium italic">
                        Create your first strand to organize your learning area content.
                    </p>
                    <button
                        onClick={handleCreateModule}
                        className="px-8 py-3 bg-[#18216D] text-white rounded-xl hover:bg-[#0D164F] transition-all font-black shadow-lg shadow-indigo-900/20 uppercase tracking-widest text-xs"
                    >
                        Edit Curriculum
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {modules.map((module, index) => (
                        <ModuleCard
                            key={module.id}
                            module={module}
                            courseId={courseId}
                            onEdit={handleEditModule}
                            onDelete={handleDeleteModule}
                            onRefresh={fetchModules}
                            onMoveUp={() => handleMoveStrand(index, 'up')}
                            onMoveDown={() => handleMoveStrand(index, 'down')}
                            isFirst={index === 0}
                            isLast={index === modules.length - 1}
                        />
                    ))}
                </div>

            )}

            {/* Module Builder Modal */}
            {showBuilder && (
                <ModuleBuilder
                    courseId={courseId}
                    module={editingModule}
                    onClose={() => {
                        setShowBuilder(false);
                        setEditingModule(null);
                    }}
                    onSave={fetchModules}
                />
            )}

            {/* Deletion Modal */}
            <Modal
                isOpen={!!strandToDelete}
                onClose={() => setStrandToDelete(null)}
                title="Remove Strand"
                type="danger"
                footer={
                    <>
                        <button
                            onClick={confirmDeleteStrand}
                            className="px-10 py-5 bg-rose-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-rose-600 transition-all shadow-xl shadow-rose-900/20"
                        >
                            Hide Strand
                        </button>
                        <button
                            onClick={() => setStrandToDelete(null)}
                            className="px-10 py-5 bg-slate-100 text-[#18216D] rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 transition-all"
                        >
                            Cancel
                        </button>
                    </>
                }
            >
                Are you sure you want to remove <span className="text-rose-600 font-black italic">"{strandToDelete?.title}"</span>?
                <div className="mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 text-xs font-bold text-slate-500 not-italic">
                    <span className="text-[#18216D] font-black uppercase tracking-widest block mb-2">Registry Safety Notice</span>
                    This action will only hide the strand from your current course view. It will <span className="text-[#18216D]">not</span> be deleted from the National Curriculum Registry.
                </div>
            </Modal>

        </div>
    );
};

export default ModuleList;
